/**
 * HH · Productos por categoría: HH · Grid por categoría (sections/hh-grid-categorias.liquid) y
 * HH · Más vendidos (sections/hh-mas-vendidos.liquid, en carrusel).
 *
 * - Pastillas como pestañas: al tocar una, se muestran sus productos sin salir de la página.
 *   La primera categoría viene en la página; las demás se piden a su colección
 *   (/collections/<handle>?section_id=hh-grid-items) y quedan guardadas. El pedido sale antes,
 *   cuando el dedo o el mouse llegan a la pastilla, y la segunda categoría se pide en segundo
 *   plano cuando la sección está por verse (no con «ahorro de datos»). Si falla, se va a la colección.
 * - Flechas, Inicio y Fin para moverse entre pastillas con el teclado.
 * - «+» agrega al carrito los productos de una sola variante y avisa al carrito del tema
 *   (se abre el carrito lateral y se actualiza el contador).
 * - Carrusel (data-hh-anterior / data-hh-siguiente): las flechas mueven la fila una pantalla y se
 *   apagan en los extremos. Al cambiar de categoría, la fila vuelve al principio.
 */

import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const AGREGADO_MS = 2200;

/** @type {Map<string, Promise<HTMLElement[]>>} */
const pedidos = new Map();

/**
 * @param {string} evento
 * @param {Record<string, unknown>} [datos]
 */
function track(evento, datos = {}) {
  try {
    (window.dataLayer = window.dataLayer || []).push({ event: evento, ...datos });
  } catch (error) {}
}

/**
 * Los productos de una colección con la tarjeta del grid.
 * @param {string} url
 * @returns {Promise<HTMLElement[]>}
 */
function pedirProductos(url) {
  let pedido = pedidos.get(url);
  if (!pedido) {
    const destino = new URL(url, window.location.origin);
    destino.searchParams.set('section_id', 'hh-grid-items');
    pedido = fetch(destino.toString(), { headers: { Accept: 'text/html' } })
      .then((respuesta) => {
        if (!respuesta.ok) throw new Error(`HTTP ${respuesta.status}`);
        return respuesta.text();
      })
      .then((html) => {
        const plantilla = document.createElement('template');
        plantilla.innerHTML = html;
        return /** @type {HTMLElement[]} */ (Array.from(plantilla.content.querySelectorAll('[data-hh-items] > .hh-gc__prod')));
      })
      .catch((error) => {
        pedidos.delete(url);
        throw error;
      });
    pedidos.set(url, pedido);
  }
  return pedido;
}

class HhGridCategorias extends HTMLElement {
  /** @type {AbortController | undefined} */
  controller;
  /** @type {IntersectionObserver | undefined} */
  observer;
  version = 0;
  /** @type {Map<string, HTMLElement[]>} Productos ya mostrados por colección. */
  guardados = new Map();

  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.chipsBox = /** @type {HTMLElement | null} */ (this.querySelector('[data-hh-chips]'));
    this.grid = /** @type {HTMLElement | null} */ (this.querySelector('[data-hh-grid]'));
    this.panel = /** @type {HTMLElement | null} */ (this.querySelector('[data-hh-panel]'));
    this.max = Number(this.dataset.max) || 4;
    this.ocultarAgotados = this.dataset.ocultarAgotados === 'true';

    const activa = this.chips().find((chip) => chip.getAttribute('aria-selected') === 'true');
    if (activa && this.grid) this.guardados.set(activa.dataset.url || '', this.productos());

    this.addEventListener('click', this.onClick, { signal });
    this.addEventListener('keydown', this.onKeydown, { signal });
    this.chipsBox?.addEventListener('pointerenter', this.onIntencion, { signal, capture: true });
    this.chipsBox?.addEventListener('touchstart', this.onIntencion, { signal, capture: true, passive: true });
    this.chipsBox?.addEventListener('focusin', this.onIntencion, { signal });
    this.chipsBox?.addEventListener('scroll', this.marcarDeslizable, { signal, passive: true });
    window.addEventListener('resize', this.marcarDeslizable, { signal, passive: true });
    document.addEventListener('shopify:block:select', this.onBlockSelect, { signal });
    this.marcarDeslizable();
    this.precargarSiguiente();

    this.anterior = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-hh-anterior]'));
    this.siguiente = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-hh-siguiente]'));
    if (this.anterior || this.siguiente) {
      this.grid?.addEventListener('scroll', this.actualizarFlechas, { signal, passive: true });
      window.addEventListener('resize', this.actualizarFlechas, { signal, passive: true });
      this.actualizarFlechas();
    }
  }

  /** Apaga la flecha del lado al que ya no se puede avanzar. */
  actualizarFlechas = () => {
    const grid = this.grid;
    if (!grid) return;
    const fin = grid.scrollWidth - grid.clientWidth;
    const inicio = Math.abs(grid.scrollLeft) <= 2;
    const final = Math.abs(grid.scrollLeft) >= fin - 2;
    if (this.anterior) this.anterior.disabled = inicio;
    if (this.siguiente) this.siguiente.disabled = final || fin <= 2;
  };

  /** @param {number} direccion - 1 hacia adelante, -1 hacia atrás */
  desplazar(direccion) {
    const grid = this.grid;
    if (!grid) return;
    const rtl = getComputedStyle(grid).direction === 'rtl' ? -1 : 1;
    grid.scrollBy({ left: direccion * rtl * grid.clientWidth * 0.9, behavior: this.suave() ? 'smooth' : 'auto' });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }

  /** Cuando la sección está por verse, pide en segundo plano la segunda categoría (la más tocada). */
  precargarSiguiente() {
    // @ts-ignore: Network Information API
    if (!('IntersectionObserver' in window) || navigator.connection?.saveData) return;
    this.observer = new IntersectionObserver(
      (entradas) => {
        if (!entradas.some((entrada) => entrada.isIntersecting)) return;
        this.observer?.disconnect();
        const precargar = () => {
          const siguiente = this.chips().find((chip) => chip.getAttribute('aria-selected') !== 'true');
          const url = siguiente?.dataset.url;
          if (url && !this.guardados.has(url)) pedirProductos(url).catch(() => {});
        };
        if ('requestIdleCallback' in window) window.requestIdleCallback(precargar, { timeout: 2000 });
        else window.setTimeout(precargar, 600);
      },
      { rootMargin: '200px 0px' }
    );
    this.observer.observe(this);
  }

  /** @returns {HTMLAnchorElement[]} */
  chips() {
    return /** @type {HTMLAnchorElement[]} */ (Array.from(this.querySelectorAll('.hh-gc__chip[role="tab"]')));
  }

  /** @returns {HTMLElement[]} */
  productos() {
    return this.grid ? /** @type {HTMLElement[]} */ (Array.from(this.grid.querySelectorAll(':scope > .hh-gc__prod'))) : [];
  }

  /** Difumina el borde de las pastillas mientras queden más a la derecha. */
  marcarDeslizable = () => {
    const box = this.chipsBox;
    if (!box) return;
    const mas = box.scrollWidth - box.clientWidth - box.scrollLeft > 4;
    box.classList.toggle('is-deslizable', mas);
  };

  /** @param {Event} event */
  onIntencion = (event) => {
    const chip = event.target instanceof Element ? event.target.closest('.hh-gc__chip') : null;
    if (!(chip instanceof HTMLAnchorElement) || !this.contains(chip)) return;
    const url = chip.dataset.url || '';
    if (url && !this.guardados.has(url)) pedirProductos(url).catch(() => {});
  };

  /** @param {MouseEvent} event */
  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const chip = target.closest('.hh-gc__chip');
    if (chip instanceof HTMLAnchorElement && this.contains(chip)) {
      // Con Ctrl/Cmd o la rueda se abre la colección como cualquier enlace.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();
      this.activar(chip);
      return;
    }

    const flecha = target.closest('[data-hh-anterior], [data-hh-siguiente]');
    if (flecha instanceof HTMLButtonElement && this.contains(flecha)) {
      this.desplazar(flecha.hasAttribute('data-hh-siguiente') ? 1 : -1);
      return;
    }

    const agregar = target.closest('button[data-hh-agregar]');
    if (agregar instanceof HTMLButtonElement && this.contains(agregar)) {
      event.preventDefault();
      this.agregar(agregar);
    }
  };

  /** @param {KeyboardEvent} event */
  onKeydown = (event) => {
    const chip = event.target instanceof Element ? event.target.closest('.hh-gc__chip') : null;
    if (!(chip instanceof HTMLAnchorElement)) return;
    const chips = this.chips();
    const i = chips.indexOf(chip);
    let siguiente = -1;
    if (event.key === 'ArrowRight') siguiente = (i + 1) % chips.length;
    else if (event.key === 'ArrowLeft') siguiente = (i - 1 + chips.length) % chips.length;
    else if (event.key === 'Home') siguiente = 0;
    else if (event.key === 'End') siguiente = chips.length - 1;
    else if (event.key === ' ') siguiente = i;
    if (siguiente < 0) return;
    event.preventDefault();
    const destino = chips[siguiente];
    destino.focus();
    this.activar(destino);
  };

  /** En el editor del tema, al elegir un bloque se abre esa categoría. */
  onBlockSelect = (/** @type {Event} */ event) => {
    const chip = event.target instanceof Element ? event.target.closest('.hh-gc__chip') : null;
    if (chip instanceof HTMLAnchorElement && this.contains(chip)) this.activar(chip);
  };

  /** @param {HTMLAnchorElement} chip */
  async activar(chip) {
    if (chip.getAttribute('aria-selected') === 'true') return;
    const url = chip.dataset.url || chip.href;
    const nombre = chip.dataset.nombre || chip.textContent?.trim() || '';
    const total = chip.dataset.total || '';

    for (const otra of this.chips()) {
      const elegida = otra === chip;
      otra.setAttribute('aria-selected', String(elegida));
      otra.tabIndex = elegida ? 0 : -1;
    }
    this.panel?.setAttribute('aria-labelledby', chip.id);
    chip.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: this.suave() ? 'smooth' : 'auto' });
    this.actualizarEnlaces(url, nombre, total);
    track('hh_grid_categoria', { categoria: nombre, seccion: this.dataset.seccion || 'grid' });

    const version = ++this.version;
    const guardados = this.guardados.get(url);
    if (guardados) {
      this.mostrar(guardados);
      return;
    }

    this.mostrarEsqueleto();
    try {
      const recibidos = await pedirProductos(url);
      const elegidos = recibidos
        .filter((fila) => !this.ocultarAgotados || fila.dataset.disponible === 'true')
        .slice(0, this.max)
        .map((fila) => /** @type {HTMLElement} */ (document.importNode(fila, true)));
      this.guardados.set(url, elegidos);
      if (version !== this.version) return;
      this.mostrar(elegidos);
      this.anunciar(`${elegidos.length} productos de ${nombre}`);
    } catch (error) {
      if (version === this.version) window.location.href = url;
    }
  }

  /** @param {HTMLElement[]} filas */
  mostrar(filas) {
    const grid = this.grid;
    if (!grid) return;
    const todo = grid.querySelector(':scope > .hh-gc__todo-item');
    for (const fila of this.productos()) fila.remove();
    grid.prepend(...filas);
    if (todo) grid.append(todo);
    grid.removeAttribute('aria-busy');
    grid.scrollLeft = 0;
    this.actualizarFlechas();
  }

  mostrarEsqueleto() {
    const grid = this.grid;
    const plantilla = /** @type {HTMLTemplateElement | null} */ (this.querySelector('template[data-hh-esqueleto]'));
    if (!grid || !plantilla) return;
    const filas = Array.from({ length: this.max }, () => {
      const fila = plantilla.content.firstElementChild?.cloneNode(true);
      return /** @type {HTMLElement} */ (fila);
    }).filter(Boolean);
    const todo = grid.querySelector(':scope > .hh-gc__todo-item');
    for (const fila of this.productos()) fila.remove();
    grid.prepend(...filas);
    if (todo) grid.append(todo);
    grid.setAttribute('aria-busy', 'true');
    grid.scrollLeft = 0;
  }

  /**
   * @param {string} url
   * @param {string} nombre
   * @param {string} total
   */
  actualizarEnlaces(url, nombre, total) {
    const texto = (/** @type {string | undefined} */ plantilla, /** @type {string} */ porDefecto) =>
      (plantilla || porDefecto).replace('[categoria]', nombre).replace('[n]', total);

    const arriba = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-hh-ver-arriba]'));
    if (arriba) arriba.href = url;
    const arribaTxt = this.querySelector('[data-hh-ver-arriba-txt]');
    if (arribaTxt) arribaTxt.textContent = texto(this.dataset.txtVer, 'Ver todo en [categoria]');
    const arribaCtx = this.querySelector('[data-hh-ver-arriba-ctx]');
    if (arribaCtx) arribaCtx.textContent = `: ${nombre}`;

    const ver = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-hh-ver]'));
    if (ver) ver.href = url;
    const verTxt = this.querySelector('[data-hh-ver-txt]');
    if (verTxt) verTxt.textContent = texto(this.dataset.txtVerTotal, 'Ver todo en [categoria] ([n])');

    const todo = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-hh-todo]'));
    if (todo) todo.href = url;
    const num = this.querySelector('[data-hh-todo-num]');
    if (num) num.textContent = total;
    const cat = this.querySelector('[data-hh-todo-cat]');
    if (cat) cat.textContent = texto(this.dataset.txtTodo, 'productos en [categoria]');
  }

  suave() {
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** @param {HTMLButtonElement} boton */
  async agregar(boton) {
    if (boton.getAttribute('aria-busy') === 'true') return;
    const id = Number(boton.dataset.hhAgregar);
    if (!id) return;
    const etiqueta = boton.getAttribute('aria-label') || '';
    const nombre = etiqueta.replace(/^Agregar /, '').replace(/ al carrito$/, '');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);

    boton.setAttribute('aria-busy', 'true');
    const evento = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(id), quantity: 1 }],
        promise: evento.promise,
      })
    );

    try {
      /** @type {Record<string, unknown>} */
      const body = { items: [{ id, quantity: 1 }] };
      if (sectionIds.length) {
        body.sections = sectionIds.join(',');
        body.sections_url = window.location.pathname;
      }
      const respuesta = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await respuesta.json();
      const carrito = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
      const error = !respuesta.ok || Boolean(data.status);

      if (error) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.description || data.message || 'No se pudo agregar',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
      }

      evento.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(carrito),
        detail: {
          items: carrito.items,
          source: 'hh-grid-categorias',
          sourceId: this.id || 'hh-grid-categorias',
          itemCount: error ? 0 : 1,
          sections: error ? undefined : data.sections,
          didError: error,
        },
      });

      boton.removeAttribute('aria-busy');
      if (error) {
        this.anunciar(data.description || data.message || `No pudimos agregar ${nombre}. Intenta de nuevo.`);
        return;
      }

      track('hh_grid_agregar', { producto: nombre, seccion: this.dataset.seccion || 'grid' });
      boton.classList.add('is-agregado');
      boton.setAttribute('aria-label', `${nombre} agregado al carrito`);
      this.anunciar(`${nombre} agregado al carrito`);
      window.setTimeout(() => {
        boton.classList.remove('is-agregado');
        boton.setAttribute('aria-label', etiqueta);
      }, AGREGADO_MS);
    } catch (error) {
      evento.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      boton.removeAttribute('aria-busy');
      this.anunciar(`No pudimos agregar ${nombre}. Revisa tu conexión e intenta de nuevo.`);
    }
  }

  /** @param {string} texto */
  anunciar(texto) {
    const estado = this.querySelector('[data-hh-estado]');
    if (estado) estado.textContent = texto;
  }
}

if (!customElements.get('hh-grid-categorias')) {
  customElements.define('hh-grid-categorias', HhGridCategorias);
}
